import {makeAutoObservable} from 'mobx'
import {Time} from 'yougile-util'
import {
  DataObjectId,
  getTaskUserEvents,
  Hub,
  TimelineFilter,
  TimelineFilterBackend,
  UserEventsByDay,
  UserEventsByTime
} from 'yougile-model'
import {Sync} from '@client/sync'
import {
  UserEventsClient,
  UserEventsClientDataLimited
} from '@client/user-events-client'
import {
  eventsGroupByDayForTaskLog,
  filterEvents,
  getActiveFilter,
  getFiltersForBackend
} from '@components/statistics/stats-timeline/utils/utils'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {withSyntheticEvents} from './utils'

const createEmptyFilter = (): TimelineFilter => ({
  createdAt: null,
  action: []
})

const itemsToLoad = 50

type ToggleView = 'table' | 'card'

interface State {
  task: Hub
  stats: UserEventsClientDataLimited
  view: ToggleView
  filter: TimelineFilter
  itemsToLoad: number
  isFetching: boolean
  isEventsLeft: boolean
}

export class TaskEventLogStore {
  state: State = {
    task: null,
    stats: null,
    view: 'card',
    filter: createEmptyFilter(),
    itemsToLoad: itemsToLoad,
    isFetching: false,
    isEventsLeft: null
  }

  constructor() {
    makeAutoObservable(this, {}, {autoBind: true})
  }

  async updateStats(taskId: DataObjectId) {
    if (!taskId) {
      return
    }
    const box = Sync.getCurrentSandbox()
    const task = box.getHub(taskId)
    if (!task) {
      return
    }
    this.state.task = task
    const startDate = this.filtersForBackend?.createdAt?.start ?? 0

    if (startDate < task?.timestamp) {
      const stats = await UserEventsClient.getDataLimited(
        globals.getCompanyId(),
        this.state.itemsToLoad,
        {
          ...this.filtersForBackend,
          createdAt: {
            ...this.filtersForBackend.createdAt,
            start: task.timestamp - Time.day
          }
        },
        taskId
      )
      this.setStats(stats)
    } else {
      const stats = await UserEventsClient.getDataLimited(
        globals.getCompanyId(),
        this.state.itemsToLoad,
        this.filtersForBackend,
        taskId
      )
      this.setStats(stats)
    }
  }

  setStats(result: UserEventsClientDataLimited) {
    this.state.stats = result
    this.state.isFetching = false
    this.state.isEventsLeft = result.isEventsLeft
  }

  get activeFilter(): Array<keyof TimelineFilter> {
    this.resetItemsToLoad()
    return getActiveFilter(this.state.filter)
  }

  get filtersForBackend(): TimelineFilterBackend {
    return {
      ...getFiltersForBackend(this.state.filter, this.activeFilter),
      ...(this.state.filter.action?.length
        ? {}
        : {
            action: getTaskUserEvents()
          })
    }
  }

  get events(): UserEventsByTime {
    if (!this.state.stats) {
      return {}
    }
    const {events} = this.state.stats
    const filteredEvents = filterEvents(events)
    return withSyntheticEvents(
      this.state.task,
      filteredEvents,
      this.filtersForBackend
    )
  }

  get eventsByDay(): UserEventsByDay {
    if (!this.events) {
      return {}
    }
    return eventsGroupByDayForTaskLog(this.events)
  }

  loadMore(): void {
    this.state.itemsToLoad = this.state.itemsToLoad + itemsToLoad
    this.state.isFetching = true
  }

  resetItemsToLoad(): void {
    this.state.stats = null
    this.state.isFetching = true
    this.state.itemsToLoad = itemsToLoad
  }

  setActiveFilterForce(filter: TimelineFilter) {
    this.state.filter = filter
  }
}

const [TaskEventLogContext, useTaskEventLogStore] =
  createUseStore(TaskEventLogStore)
export {TaskEventLogContext, useTaskEventLogStore}
